import type { LocalDatabaseHealth } from './types';

export const OFFLINE_DB_NAME = 'coconnect-offline';
export const OFFLINE_DB_VERSION = 1;
export const OFFLINE_SCHEMA_VERSION = '1.0.0';

export type OfflineStoreName =
  | 'meta'
  | 'domain_records'
  | 'outbox_commands'
  | 'sync_acknowledgements'
  | 'audit_events'
  | 'conflicts'
  | 'reference_data'
  | 'attachments'
  | 'offline_auth_envelopes'
  | 'device_state'
  | 'client_health'
  | 'workflow_checkpoints'
  | 'driver_gps_events';

let dbPromise: Promise<IDBDatabase> | null = null;
let currentHealth: LocalDatabaseHealth = 'HEALTHY';
let lastDbError: unknown = null;

function createStores(db: IDBDatabase): void {
  if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta', { keyPath: 'key' });

  if (!db.objectStoreNames.contains('domain_records')) {
    const store = db.createObjectStore('domain_records', { keyPath: 'local_key' });
    store.createIndex('byTenantUserType', ['tenant_id', 'user_id', 'resource_type'], { unique: false });
    store.createIndex('bySyncStatus', ['tenant_id', 'user_id', 'sync_status'], { unique: false });
    store.createIndex('byUpdatedAt', 'updated_at', { unique: false });
  }

  if (!db.objectStoreNames.contains('outbox_commands')) {
    const store = db.createObjectStore('outbox_commands', { keyPath: 'outbox_id' });
    store.createIndex('byCommandId', 'command_id', { unique: true });
    store.createIndex('byIdempotencyKey', 'idempotency_key', { unique: true });
    store.createIndex('byStatusPriority', ['status', 'priority', 'sequence_no'], { unique: false });
    store.createIndex('byTenantUser', ['tenant_id', 'user_id'], { unique: false });
    store.createIndex('byResource', ['resource_type', 'resource_id'], { unique: false });
    store.createIndex('byNextAttempt', 'next_attempt_at', { unique: false });
  }

  if (!db.objectStoreNames.contains('sync_acknowledgements')) {
    const store = db.createObjectStore('sync_acknowledgements', { keyPath: 'ack_id' });
    store.createIndex('byCommandId', 'command_id', { unique: true });
    store.createIndex('byResource', ['resource_type', 'resource_id'], { unique: false });
  }

  if (!db.objectStoreNames.contains('audit_events')) {
    const store = db.createObjectStore('audit_events', { keyPath: 'audit_id' });
    store.createIndex('byTenantUserSequence', ['tenant_id', 'user_id', 'sequence'], { unique: true });
    store.createIndex('byResource', ['resource_type', 'resource_id'], { unique: false });
    store.createIndex('bySyncStatus', 'sync_status', { unique: false });
  }

  if (!db.objectStoreNames.contains('conflicts')) {
    const store = db.createObjectStore('conflicts', { keyPath: 'conflict_id' });
    store.createIndex('byStatus', 'status', { unique: false });
    store.createIndex('byResource', ['resource_type', 'resource_id'], { unique: false });
    store.createIndex('byTenantUser', ['tenant_id', 'user_id'], { unique: false });
  }

  if (!db.objectStoreNames.contains('reference_data')) {
    const store = db.createObjectStore('reference_data', { keyPath: 'local_key' });
    store.createIndex('byDataset', ['tenant_id', 'user_id', 'dataset'], { unique: false });
    store.createIndex('byEffectiveAt', 'effective_at', { unique: false });
  }

  if (!db.objectStoreNames.contains('attachments')) {
    const store = db.createObjectStore('attachments', { keyPath: 'attachment_id' });
    store.createIndex('byOwner', ['tenant_id', 'user_id'], { unique: false });
    store.createIndex('byStatus', 'sync_status', { unique: false });
    store.createIndex('byResource', ['resource_type', 'resource_id'], { unique: false });
  }

  if (!db.objectStoreNames.contains('offline_auth_envelopes')) {
    const store = db.createObjectStore('offline_auth_envelopes', { keyPath: 'envelope_id' });
    store.createIndex('byUserDevice', ['user_id', 'device_id'], { unique: false });
    store.createIndex('byExpiry', 'offline_valid_until', { unique: false });
  }

  if (!db.objectStoreNames.contains('device_state')) db.createObjectStore('device_state', { keyPath: 'device_id' });
  if (!db.objectStoreNames.contains('client_health')) db.createObjectStore('client_health', { keyPath: 'id' });

  if (!db.objectStoreNames.contains('workflow_checkpoints')) {
    const store = db.createObjectStore('workflow_checkpoints', { keyPath: 'workflow_id' });
    store.createIndex('byOwnerState', ['tenant_id', 'user_id', 'state'], { unique: false });
    store.createIndex('byUpdatedAt', 'updated_at', { unique: false });
  }

  if (!db.objectStoreNames.contains('driver_gps_events')) {
    const store = db.createObjectStore('driver_gps_events', { keyPath: 'event_id' });
    store.createIndex('byStatusSequence', ['sync_status', 'sequence_no'], { unique: false });
    store.createIndex('byDispatch', 'dispatch_id', { unique: false });
    store.createIndex('byOwner', ['tenant_id', 'user_id', 'device_id'], { unique: false });
  }
}

export function openOfflineDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      currentHealth = 'CORRUPT';
      const error = new Error('IndexedDB is unavailable in this browser');
      lastDbError = error;
      reject(error);
      return;
    }
    const request = indexedDB.open(OFFLINE_DB_NAME, OFFLINE_DB_VERSION);
    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      try {
        createStores(request.result);
        const tx = request.transaction;
        if (tx) {
          const meta = tx.objectStore('meta');
          meta.put({ key: 'local_schema_version', value: OFFLINE_SCHEMA_VERSION, updated_at: new Date().toISOString() });
          meta.put({ key: 'db_version', value: OFFLINE_DB_VERSION, updated_at: new Date().toISOString() });
          meta.put({ key: 'migration_journal:last', value: { from: event.oldVersion, to: event.newVersion, status: 'APPLYING' }, updated_at: new Date().toISOString() });
          tx.onabort = () => {
            currentHealth = 'RECOVERY_REQUIRED';
            lastDbError = tx.error;
          };
        }
      } catch (error) {
        currentHealth = 'RECOVERY_REQUIRED';
        lastDbError = error;
        throw error;
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      currentHealth = 'HEALTHY';
      // Finalize the journal outside the upgrade transaction so a successful open
      // is distinguishable from an interrupted upgrade.
      const tx = db.transaction('meta', 'readwrite');
      tx.objectStore('meta').put({ key: 'migration_journal:last', value: { to: db.version, status: 'APPLIED' }, updated_at: new Date().toISOString() });
      resolve(db);
    };
    request.onerror = () => {
      currentHealth = 'RECOVERY_REQUIRED';
      lastDbError = request.error;
      dbPromise = null;
      reject(request.error || new Error('Failed to open offline database'));
    };
    request.onblocked = () => {
      currentHealth = 'DEGRADED';
      lastDbError = new Error('Offline database upgrade is blocked by another tab');
    };
  });
  return dbPromise;
}

export function offlineDbHealth(): { state: LocalDatabaseHealth; error: unknown } {
  return { state: currentHealth, error: lastDbError };
}


/** Public database health accessor used by diagnostics/status surfaces. */
export function getDatabaseHealth(): { state: LocalDatabaseHealth; error: unknown } {
  return offlineDbHealth();
}

/** Read the persisted local schema version; fall back to the code-declared version only when the meta row is absent. */
export async function getLocalSchemaVersion(): Promise<string> {
  const row = await getRecord<{ key: string; value?: unknown }>('meta', 'local_schema_version');
  return typeof row?.value === 'string' && row.value.trim() ? row.value : OFFLINE_SCHEMA_VERSION;
}

export async function withOfflineTransaction<T>(
  stores: OfflineStoreName[],
  mode: IDBTransactionMode,
  work: (tx: IDBTransaction) => Promise<T> | T,
): Promise<T> {
  const db = await openOfflineDb();
  return new Promise<T>((resolve, reject) => {
    let result: T;
    let workFinished = false;
    let workFailed = false;
    let tx: IDBTransaction;
    try {
      // Chromium supports the durability hint, but older/mobile engines may only
      // implement the two-argument IndexedDB transaction signature. Strict
      // durability is therefore requested where supported without making the
      // local commit depend on that optional Web API extension.
      tx = (db as any).transaction(stores, mode, { durability: mode === 'readwrite' ? 'strict' : 'default' });
    } catch {
      tx = db.transaction(stores, mode);
    }
    tx.oncomplete = () => {
      if (workFinished && !workFailed) resolve(result!);
    };
    tx.onerror = () => {
      currentHealth = 'DEGRADED';
      lastDbError = tx.error;
      reject(tx.error || new Error('IndexedDB transaction failed'));
    };
    tx.onabort = () => {
      currentHealth = 'DEGRADED';
      lastDbError = tx.error;
      reject(tx.error || new Error('IndexedDB transaction aborted'));
    };
    Promise.resolve(work(tx)).then(value => {
      result = value;
      workFinished = true;
      // The transaction may already have completed for synchronous work. If so,
      // IDB has queued completion after requests; resolve in tx.oncomplete.
    }).catch(error => {
      workFailed = true;
      try { tx.abort(); } catch { /* already inactive */ }
      reject(error);
    });
  });
}

export function idbRequest<T = unknown>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
  });
}

export async function putRecord<T>(store: OfflineStoreName, value: T): Promise<void> {
  await withOfflineTransaction([store], 'readwrite', async tx => {
    await idbRequest(tx.objectStore(store).put(value));
  });
}

export async function getRecord<T>(store: OfflineStoreName, key: IDBValidKey): Promise<T | undefined> {
  return withOfflineTransaction([store], 'readonly', tx => idbRequest(tx.objectStore(store).get(key)) as Promise<T | undefined>);
}

export async function getAllByIndex<T>(store: OfflineStoreName, index: string, query?: IDBValidKey | IDBKeyRange | null, count?: number): Promise<T[]> {
  return withOfflineTransaction([store], 'readonly', async tx => {
    const request = tx.objectStore(store).index(index).getAll(query ?? null, count);
    return (await idbRequest(request)) as T[];
  });
}

export async function deleteRecord(store: OfflineStoreName, key: IDBValidKey): Promise<void> {
  await withOfflineTransaction([store], 'readwrite', async tx => {
    await idbRequest(tx.objectStore(store).delete(key));
  });
}
